import { Injectable } from '@angular/core';
import { WebService } from '../web/web.service';
import { normalizeExternalUrl } from '../shared/util/external-url.util';

export const CARD_LINK_TYPE = {
  SOCIAL_NETWORK: 'social_network',
  ADDRESS_MAP: 'address_map',
  CUSTOM_LINK: 'custom_link',
  IMAGE_CAROUSEL: 'image_carousel',
  FOOTER_IMAGE: 'footer_image',
} as const;


@Injectable({
  providedIn: 'root'
})
export class CardLinkAnalyticsService {

  readonly LINK_TYPE = CARD_LINK_TYPE;
  private lastOpenedHref = '';
  private lastOpenedAt = 0;

  constructor(private webService: WebService) {}

  private shouldSkipTrackingForCurrentRoute(): boolean {
    const path = (window.location.pathname || '').toLowerCase();
    return path.includes('/user/card/view');
  }

  /**
   * Track external link click and open URL.
   * @param cardNo Full card identifier (e.g. card_no from API)
   * @param linkType One of: social_network, address_map, custom_link, image_carousel, footer_image
   * @param href URL to open
   * @param linkItem Optional label (e.g. Twitter, product title, custom link label)
   */
  private normalizeExternalHref(href: string): string {
    const normalized = normalizeExternalUrl(href);
    if (!normalized) {
      return '';
    }

    try {
      const parsed = new URL(normalized);
      const allowedProtocols = ['http:', 'https:', 'mailto:', 'tel:'];
      return allowedProtocols.includes(parsed.protocol) ? parsed.toString() : '';
    } catch {
      return '';
    }
  }

  trackLinkClick(cardNo: string, linkType: string, href: string, linkItem?: string): void {
    if (cardNo && !this.shouldSkipTrackingForCurrentRoute()) {
      this.webService.publicTrackClick(cardNo, linkType, linkItem ?? undefined).subscribe();
    }

    const safeHref = this.normalizeExternalHref(href);
    if (!safeHref) {
      return;
    }

    const now = Date.now();
    const isDuplicateOpen = this.lastOpenedHref === safeHref && now - this.lastOpenedAt < 800;
    if (isDuplicateOpen) {
      return;
    }

    this.lastOpenedHref = safeHref;
    this.lastOpenedAt = now;
    this.openExternalUrl(safeHref);
  }

  /**
   * Opens http(s) links in a new tab without `window.open`'s third argument (window "features"),
   * which on many mobile browsers opens an extra blank tab alongside the real navigation.
   * mailto:/tel: use same-tab navigation so system handlers open without a blank page.
   */
  private openExternalUrl(url: string): void {
    let protocol = '';
    try {
      protocol = new URL(url).protocol;
    } catch {
      return;
    }

    if (protocol === 'mailto:' || protocol === 'tel:') {
      window.location.assign(url);
      return;
    }

    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  getSocialLabel(item: { type?: number; label?: string } | null | undefined): string {
    if (item?.label) {
      return item.label;
    }
    const names: Record<number, string> = {
      1: 'Facebook', 2: 'Twitter', 3: 'Instagram', 4: 'LinkedIn', 5: 'WhatsApp',
      6: 'Messenger', 7: 'Skype', 8: 'YouTube', 9: 'Pinterest', 10: 'TikTok', 11: 'Telegram', 12: 'SnapChat'
    };
    return (item?.type != null && names[item.type]) ? names[item.type] : '';
  }

  getProductLabel(product: { productDetails?: Array<{ language?: string; title?: string }> } | null | undefined, lang: string): string {
    const detail = product?.productDetails?.find(d => d?.language === lang);
    return detail?.title ?? '';
  }

  getCustomLinkLabel(item: { label1?: Array<{ language?: string; label?: string; description?: string }> } | null | undefined, lang: string): string {
    const l = item?.label1?.find(x => x?.language === lang);
    return (l?.label ?? l?.description ?? '') || '';
  }

  getFooterImageLabel(cardDetails: { custom?: { imageDesc?: Array<{ language?: string; title?: string }> } } | null | undefined, lang: string): string {
    const item = cardDetails?.custom?.imageDesc?.find(d => d?.language === lang);
    return item?.title ?? '';
  }
}
